import { CustomAttributeRoute } from "#routes/settings/custom-attributes";
import { useLocation, useNavigate } from "#src/Routers/hooks";
import { linkToCompanySettings, linkToUserSetting } from "#src/Routers/links";
import { AccountChip } from "#src/batteries-included-components/Layouts/Authentication/AccountChip";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { AssistantGlobal } from "#src/components/Assistant/AssistantGlobal";
import { GlobalRefreshTrigger } from "#src/components/Common/GlobalRefreshTrigger";
import { GlobalWorkerRunButton } from "#src/components/Common/GlobalWorkerRunButton/GlobalWorkerRunButton";
import config from "#src/config";
import { AuthenticatedContextAuthVersionType } from "#src/contexts/AuthenticatedContext";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useDefaultUserInterfaceConfiguration } from "#src/contexts/DefaultUserInterfaceConfigurationContext";
import { useOsano } from "#src/hooks/useOsano";
import { linkToActivityLogs } from "#src/routes/change-log";
import { linkToScenarioList } from "#src/routes/scenarios";
import { linkToMeasurementTypesSettingsPage } from "#src/routes/settings/measurement-types/index";
import { linkToRolesListPage } from "#src/routes/settings/roles-and-permissions";
import { linkToUsersListPage } from "#src/routes/settings/users";
import { linkToUserDetailPage } from "#src/routes/settings/users/detail";
import { linkToUserEditPage } from "#src/routes/settings/users/edit";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  colours,
  Dialog,
  DropdownMenu,
  DropdownMenuOption,
  Icon,
  Link,
  Popover,
  Tag,
  TextDataDisplay,
  Tooltip,
  useToast,
} from "@validereinc/common-components";
import { FeatureFlags, PermissionsMap } from "@validereinc/domain-controllers";
import { copyTextToClipboard, MutationError } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useState } from "react";
import { CompanySelector } from "../CompanySelector/CompanySelector";
import styles from "./AccountNavBar.module.scss";

const cx = classNames.bind(styles);

const bannerColours = [
  colours.colours.legacy.chartsHover["100"],
  colours.colours.legacy.chartsHover["400"],
  colours.colours.legacy.chartsHover["300"],
  colours.colours.legacy.chartsHover["200"],
];

export const LegacyAccountNavBarSettings = {
  ACCOUNT: "account",
  COMPANY: "company",
  HELP: "help",
  DEFAULT: "settings",
} as const;

export const AccountNavBarSettings = {
  USERS: "users",
  ROLES_AND_PERMISSIONS: "roles-and-permissions",
  CHANGE_LOG: "change-log",
  KNOWLEDGE_BASE: "knowledge-base",
  CONTACT_SUPPORT: "contact-support",
  MEASUREMENT_TYPES: "measurement-types",
  CUSTOM_ATTRIBUTES: "custom-attributes",
} as const;

export const AccountNavBar = () => {
  const {
    appVersion,
    activeAuthVersion,
    logout,
    sentryFeedbackInstance,
    v1: { user: legacyUser },
    v2: {
      userInfo: { user },
      companyInfo: { company, checkIsFeatureEnabled },
    },
  } = useAuthenticatedContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isUserRolesPermsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:roles_permissions",
  });
  const [isActivityLogsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:activity_logs",
    permissionQuery: "activities:read_all",
  });
  const {
    loadDefaultConfigurations,
    clearDefaultConfigurations,
    clearAllConfigurations,
    appliedDefaultConfigurations,
    availableDefaultConfigurations,
    isLoading: areDefaultUIConfigsLoading,
  } = useDefaultUserInterfaceConfiguration();
  const [areUIConfigsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:user_interface_configurations",
    permissionQuery: "user_interface_configurations:read",
  });
  const userCompanies = user?.companies ?? [];
  const currentCompanyIndex = userCompanies.findIndex(
    (userCompany) => userCompany.id === company?.id
  );
  const { isEnabled: isOsanoEnabled, showDrawer } = useOsano();
  const location = useLocation();

  const clearCacheMutation = useMutation({
    mutationFn: () => {
      window.localStorage.clear();
      window.sessionStorage.clear();
      queryClient.clear();

      return Promise.resolve(true);
    },
    onSuccess: () => {
      toast.push({
        intent: "success",
        description: "Successfully cleared application cache.",
      });
    },
    onError: (err) => {
      const normalizedError = new MutationError(
        "Failed to clear browser storage and network cache",
        {
          cause: err,
          getExplanation: () =>
            "Failed to clear application cache. Please try again.",
        }
      );
      toast.push({
        intent: "error",
        description: normalizedError.getExplanation(),
      });
      ExceptionUtils.reportException(
        normalizedError,
        normalizedError.severity,
        {
          sourceComponent: "AccountNavBar.tsx",
        }
      );
    },
    meta: {
      hideErrorToasts: true,
    },
  });

  const getSettingsOptions = (): DropdownMenuOption[] => {
    const applicationOptions: DropdownMenuOption[] = [
      {
        label: "Application",
        isGroup: true,
        options: [
          {
            label: "Release Notes",
            onClick: () => window.open(config.RELEASE_NOTE_URL, "__blank"),
            slotLeft: ({ iconProps }) => (
              <Icon
                variant="package"
                {...iconProps}
              />
            ),
          },
          {
            label: "Public API",
            onClick: () => window.open(config.API_DOCS_URL, "__blank"),
            slotLeft: ({ iconProps }) => (
              <Icon
                variant="code"
                {...iconProps}
              />
            ),
          },
          {
            label: "Terms & Conditions",
            onClick: () => window.open(config.TERMS_OF_SERVICE_URL, "__blank"),
            slotLeft: ({ iconProps }) => (
              <Icon
                variant="file-text"
                {...iconProps}
              />
            ),
          },
          ...(isOsanoEnabled
            ? [
                {
                  label: "Cookie Settings",
                  onClick: () => showDrawer(),
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      variant="cookie"
                      {...iconProps}
                    />
                  ),
                },
              ]
            : []),
          {
            label: "Privacy Policy",
            onClick: () => window.open(config.PRIVACY_POLICY_URL, "__blank"),
            slotLeft: ({ iconProps }) => (
              <Icon
                variant="file-text"
                {...iconProps}
              />
            ),
          },
          ...(areUIConfigsAvailable
            ? ([
                {
                  label: "UI Configurations",
                  options: [
                    {
                      label: `Load Defaults ${availableDefaultConfigurations ? `(${availableDefaultConfigurations} available)` : ""}`,
                      isActive: areDefaultUIConfigsLoading,
                      slotLeft: ({ iconProps }) => (
                        <Icon
                          variant="box-arrow-down"
                          {...iconProps}
                        />
                      ),
                      onClick: () => {
                        loadDefaultConfigurations()
                          .then((appliedCount) => {
                            toast.push({
                              intent: "success",
                              description: `Successfully loaded ${appliedCount} default user interface configurations.`,
                            });
                          })
                          .catch(() => {
                            toast.push({
                              intent: "error",
                              description:
                                "Failed to load default user interface configurations. Might be a good idea to clear all and try again.",
                            });
                          });
                      },
                    },
                    {
                      label: "Clear Defaults",
                      variant: "warning",
                      isDisabled: !appliedDefaultConfigurations,
                      slotLeft: ({ iconProps }) => (
                        <Icon
                          variant="broom"
                          {...iconProps}
                        />
                      ),
                      onClick: () => {
                        clearDefaultConfigurations().then((clearedCount) => {
                          toast.push({
                            intent: "success",
                            description: `Successfully cleared ${clearedCount} default user interface configurations.`,
                          });
                        });
                      },
                    },
                    {
                      label: "Clear All",
                      variant: "warning",
                      slotLeft: ({ iconProps }) => (
                        <Icon
                          variant="broom"
                          {...iconProps}
                        />
                      ),
                      onClick: () => {
                        clearAllConfigurations();
                        toast.push({
                          intent: "success",
                          description:
                            "Successfully cleared all user interface configurations.",
                        });
                      },
                    },
                    {
                      isSeparator: true,
                    },
                    {
                      label: `Defaults Loaded: ${appliedDefaultConfigurations}`,
                      isPresentational: true,
                    },
                  ],
                },
              ] satisfies DropdownMenuOption[])
            : []),
          {
            label: "Clear Cache",
            variant: "warning",
            slotLeft: ({ iconProps }) => (
              <Icon
                variant="broom"
                {...iconProps}
              />
            ),
            onClick: () => clearCacheMutation.mutate(),
          },
        ],
      },
      {
        label: "Version",
        isPresentational: true,
        flair: appVersion,
      },
    ];

    if (!isUserRolesPermsAvailable && !isActivityLogsAvailable) {
      return applicationOptions;
    }

    return [
      {
        label: "Company",
        isGroup: true,
        options: [
          ...(isActivityLogsAvailable
            ? ([
                {
                  label: "Change Log",
                  onClick: () => navigate({ pathname: linkToActivityLogs() }),
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      variant="clock-counter-clockwise"
                      {...iconProps}
                    />
                  ),
                },
              ] satisfies DropdownMenuOption[])
            : []),
          ...(isUserRolesPermsAvailable
            ? ([
                {
                  label: "Users",
                  onClick: () => navigate({ pathname: linkToUsersListPage() }),
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      variant="users-three"
                      {...iconProps}
                    />
                  ),
                },
                {
                  label: "Roles & Permissions",
                  onClick: () => navigate({ pathname: linkToRolesListPage() }),
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      variant="seal-check"
                      {...iconProps}
                    />
                  ),
                },
                {
                  label: "Measurement Types",
                  onClick: () =>
                    navigate({
                      pathname: linkToMeasurementTypesSettingsPage(),
                    }),
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      variant="hourglass"
                      {...iconProps}
                    />
                  ),
                },
                {
                  label: "Custom Attributes",
                  onClick: () =>
                    navigate({
                      pathname: CustomAttributeRoute.path,
                    }),
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      variant="database"
                      {...iconProps}
                    />
                  ),
                },
                {
                  label: "Scenarios",
                  onClick: () =>
                    navigate({
                      pathname: linkToScenarioList(),
                    }),
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      variant="chart-bar"
                      {...iconProps}
                    />
                  ),
                },
              ] satisfies DropdownMenuOption[])
            : []),
        ],
      },
      ...applicationOptions,
    ];
  };

  const accountName =
    activeAuthVersion !== "unknown" &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v1")
      ? legacyUser?.name
      : user?.name;
  const accountEmail =
    activeAuthVersion !== "unknown" &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v1")
      ? legacyUser?.email
      : user?.email;
  const companyName =
    activeAuthVersion !== "unknown" &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v1")
      ? legacyUser?.company_name
      : company?.name;
  const isDualAuth =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 2 &&
    (["v1", "v2"] satisfies AuthenticatedContextAuthVersionType[]).every((v) =>
      activeAuthVersion.includes(v)
    );
  const isV2AuthOnly =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v2");
  const isSettingsButtonEnabled =
    (isDualAuth && !checkIsFeatureEnabled("ops:auth")) || isV2AuthOnly;

  const isOnOpsSettingsPage = [
    linkToCompanySettings(),
    linkToUserSetting(),
  ].includes(location.pathname);

  return (
    <nav
      className={cx("container", isOnOpsSettingsPage && "on-ops-settings-page")}
    >
      <div className={cx("settings-container")}>
        <div className={cx("org")}>
          {companyName ? (
            <DropdownMenu
              contentProps={{
                sideOffset: 4,
                align: "end",
              }}
              options={[
                {
                  label: "Copy ID",
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      {...iconProps}
                      variant="copy-simple"
                    />
                  ),
                  onClick: () => {
                    if (!company?.id) return;

                    copyTextToClipboard(company.id).then(() =>
                      toast.push({
                        intent: "success",
                        description:
                          "Successfully copied company ID to your clipboard.",
                      })
                    );
                  },
                },
              ]}
            >
              <button className={cx("org-trigger")}>
                <TextDataDisplay
                  value={companyName}
                  clamp={1}
                />
                <Icon
                  variant="caret-down"
                  size={16}
                  weight="bold"
                  style={{
                    flexShrink: 0,
                  }}
                />
              </button>
            </DropdownMenu>
          ) : (
            <Tooltip
              title="You're not part of a company"
              content="Talk to our support team to configure a company for your account"
            >
              <div className={cx("text-with-icon", "warning")}>
                <Icon
                  variant="warning"
                  size={16}
                />
                No Company
              </div>
            </Tooltip>
          )}
        </div>
        <IsFeatureAvailable
          featureFlagQuery={FeatureFlags.CustomCodeFeatureFlag}
          permissionQuery={PermissionsMap.WorkerRunWrite}
          fallbackChildren={null}
        >
          <GlobalWorkerRunButton />
        </IsFeatureAvailable>
        <GlobalRefreshTrigger />
        <IsFeatureAvailable
          featureFlagQuery="core:assistant"
          fallbackChildren={null}
        >
          <AssistantGlobal />
        </IsFeatureAvailable>
        <DropdownMenu
          options={[
            {
              label: "Knowledge Base",
              slotLeft: ({ iconProps }) => (
                <Icon
                  variant="lifebuoy"
                  {...iconProps}
                />
              ),
              onClick: () => window.open(config.KNOWLEDGE_BASE_URL, "__blank"),
            },
            {
              label: "Report a Bug",
              isDisabled: !sentryFeedbackInstance || !user,
              slotLeft: ({ iconProps }) => (
                <Icon
                  variant="megaphone"
                  {...iconProps}
                />
              ),
              onClick: () => {
                if (!sentryFeedbackInstance) return;

                sentryFeedbackInstance.createForm().then((f) => {
                  f.appendToDom();
                  f.open();
                });
              },
            },
            {
              label: "Contact Support",
              slotLeft: ({ iconProps }) => (
                <Icon
                  variant="headset"
                  {...iconProps}
                />
              ),
              onClick: () => setIsSupportModalOpen(true),
            },
          ]}
        >
          <Button
            iconProps={{ size: 18 }}
            icon="question"
            variant="outline"
            size="xx-small"
          />
        </DropdownMenu>
        {isSettingsButtonEnabled ? (
          <DropdownMenu options={getSettingsOptions()}>
            <Button
              iconProps={{ size: 18 }}
              icon="gear-six"
              variant="outline"
              size="xx-small"
            />
          </DropdownMenu>
        ) : null}
        <Popover
          contentRender={() => (
            <div className={cx("account-menu-container")}>
              <div
                className={cx("account-menu-banner")}
                style={{
                  backgroundColor:
                    bannerColours[currentCompanyIndex % bannerColours.length],
                }}
              ></div>
              <div className={cx("account-menu-chip-container")}>
                <AccountChip
                  name={accountName}
                  isPresentational
                  className="account-menu-chip"
                />
              </div>
              <div className={cx("account-menu-user-information-container")}>
                {accountName ? (
                  <p className={cx("account-menu-account-name")}>
                    {accountName}
                  </p>
                ) : (
                  <p
                    className={cx("account-menu-account-name update-name")}
                    onClick={() =>
                      navigate({ pathname: linkToUserEditPage(user?.id) })
                    }
                  >
                    Update your name
                  </p>
                )}

                <p className={cx("account-menu-account-email")}>
                  {accountEmail}
                </p>
                <p className={cx("account-menu-account-organization")}>
                  {companyName}
                </p>
              </div>

              <div className={cx("account-menu-button-container")}>
                <Button
                  key="manage-account-action"
                  variant="outline"
                  className={cx("account-menu-account-button")}
                  onClick={() =>
                    navigate({
                      pathname: linkToUserDetailPage(user?.id),
                    })
                  }
                  slotLeft={<Icon variant="user-circle-gear" />}
                >
                  Manage Account
                </Button>
              </div>
              <CompanySelector currentCompanyIndex={currentCompanyIndex} />
              <div className={cx("account-log-out-button-container")}>
                <Button
                  key="log-out-action"
                  variant="outline"
                  className={cx("account-menu-account-button")}
                  onClick={logout}
                  slotLeft={
                    <Icon
                      variant="sign-out"
                      className="account-menu-organization-icon"
                    />
                  }
                >
                  Log out
                </Button>
              </div>
            </div>
          )}
          shouldDisplayArrow={false}
          contentProps={{
            sideOffset: 4,
          }}
        >
          <AccountChip name={accountName} />
        </Popover>
      </div>
      <Dialog
        title="Contact our Support Team"
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        actionRow={[
          <Button
            key="dismiss"
            variant="primary"
            onClick={() => setIsSupportModalOpen(false)}
          >
            Done
          </Button>,
        ]}
      >
        <>
          <p>
            You can reach us easiest by email. If possible please include a
            detailed explanation, relevant links, screenshots/video
            demonstrating the problem, replicable processes, and any other
            information that can assist you with your help request.
          </p>
          <ul className={cx("support-options")}>
            <Tag
              as="li"
              label="Email"
              value={
                <Link
                  anchorTagProps={{
                    href: `mailto:${
                      config.SUPPORT_EMAIL
                    }?subject=Need Product Help (app: Validere Private Web)${
                      companyName ? ` (company: ${companyName})` : ""
                    }${
                      accountEmail ? ` (account email: ${accountEmail})` : ""
                    }`,
                  }}
                  label={config.SUPPORT_EMAIL}
                />
              }
            />
          </ul>
        </>
      </Dialog>
    </nav>
  );
};
